import { Currency, CurrencyAmount, Fraction, Price } from '@kodiak-finance/sdk-core'
import { DEFAULT_LOCALE, SupportedLocale } from 'constants/locales'
import JSBI from 'jsbi'
import formatLocaleNumber from 'lib/utils/formatLocaleNumber'
import numbro from 'numbro'
import { formatUnits } from 'viem'

import { formatAmount } from './formatDollarAmt'
import formatSmallAmount from './formatSmallAmount'

const DEFAULT_FORMAT = {
  thousandSeparated: true,
  mantissa: 2,
  trimMantissa: true,
  average: false,
  autoForceAverage: false,
  forceAverage: undefined as 'billion' | 'million' | undefined,
}

const LEADING_ZEROES_SYMBOLS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉']

export function formatCurrencyAmount(
  amount: CurrencyAmount<Currency> | undefined,
  sigFigs: number,
  locale: SupportedLocale = DEFAULT_LOCALE,
  fixedDecimals?: number
): string {
  if (!amount) {
    return '-'
  }

  if (JSBI.equal(amount.quotient, JSBI.BigInt(0))) {
    return '0'
  }

  if (amount.divide(amount.decimalScale).lessThan(new Fraction(1, 100000))) {
    return `<${formatLocaleNumber({ number: 0.00001, locale })}`
  }

  const baseString = parseFloat(amount.toSignificant(sigFigs))
  const numberString = fixedDecimals ? parseFloat(baseString.toFixed(fixedDecimals)) : baseString

  if (numberString > 10000000) {
    // Use formatAmount for numbers greater than 1 billion
    return formatAmount(numberString)
  } else {
    // Use formatLocaleNumber for other numbers
    return formatLocaleNumber({ number: amount, locale, sigFigs, fixedDecimals })
  }
}

export function formatPrice(
  price: Price<Currency, Currency> | undefined,
  sigFigs: number,
  locale: SupportedLocale = DEFAULT_LOCALE
): string {
  if (!price) {
    return '-'
  }

  // Convert the price to a string with significant figures without rounding
  const priceStr = price.toSignificant(sigFigs)

  // Check if the string represents a number in scientific notation
  if (priceStr.includes('e') || priceStr.includes('E') || priceStr.startsWith('0.0000')) {
    return formatSmallAmount(priceStr, sigFigs)
  }

  // Use formatLocaleNumber for other numbers
  // const priceNumber = Number(priceStr)
  const priceNumber = Number(parseFloat(priceStr))

  if (priceNumber > 10000000) {
    // Use formatAmount for numbers greater than 1 billion
    return formatAmount(priceNumber)
  } else {
    // Use formatLocaleNumber for other numbers
    return formatLocaleNumber({ number: priceNumber, locale, sigFigs })
  }
}

function leadingZeroes(n: number): string {
  return n
    .toString()
    .split('')
    .map((c) => LEADING_ZEROES_SYMBOLS[parseInt(c)])
    .join('')
}

export function formatNumber(
  v: string | number | null | undefined,
  overrides?: Partial<typeof DEFAULT_FORMAT>
): string | null | undefined
export function formatNumber(
  v: bigint | null | undefined,
  decimals?: number,
  overrides?: Partial<typeof DEFAULT_FORMAT>
): string | null | undefined
export function formatNumber(
  v: string | number | null | undefined | bigint,
  decimalsOrOverrides?: number | Partial<typeof DEFAULT_FORMAT>,
  _overrides?: Partial<typeof DEFAULT_FORMAT>
): string | null | undefined {
  if (v === null || v === undefined) return v
  if ((typeof v === 'number' && isNaN(v)) || v === 'NaN') {
    return '∞'
  }

  const decimals = typeof decimalsOrOverrides === 'number' ? decimalsOrOverrides : undefined
  const overrides = typeof decimalsOrOverrides === 'number' ? _overrides : decimalsOrOverrides

  let str: string
  if (typeof v === 'bigint') {
    str = formatUnits(v, decimals ?? 18)
  } else if (typeof v === 'number') {
    str = v.toLocaleString('en-US', {
      minimumFractionDigits: 18,
      useGrouping: false,
    })
  } else {
    str = v
  }

  const num = parseFloat(str)

  let format = { ...DEFAULT_FORMAT, ...overrides }
  const autoForceAverage = format.autoForceAverage

  if (num >= 1_000_000_000 && autoForceAverage) {
    format = { ...format, average: false, forceAverage: 'billion', autoForceAverage: false }
  } else if (num >= 1_000_000 && autoForceAverage) {
    format = {
      ...format,
      average: false,
      forceAverage: 'million',
      autoForceAverage: false,
    }
  } else if (num < 0.01) {
    const [part0, part1] = str.split('.')
    if (part1 === undefined || parseFloat(part1) === 0) {
      return part0
    }
    const leadingZerosCount = part1.match(/^0+/)?.[0]?.length || 0

    const formattedPart1 = part1
      .slice(leadingZerosCount)
      .slice(0, format.mantissa ?? 5)
      .replace(/0+$/, '')
      .padEnd(format.mantissa ?? 5, '0')
    return `${part0}.0${leadingZeroes(leadingZerosCount)}${formattedPart1}`
  }

  // Remove autoForceAverage before passing to numbro
  const { autoForceAverage: _, ...numbroFormat } = format
  if (numbroFormat.forceAverage === undefined) {
    delete (numbroFormat as any).forceAverage
  }

  const formatted = numbro(str).format(numbroFormat as any)
  return formatted ? formatted.toUpperCase() : formatted
}

export const formatPriceWithMantissa = (price: number) => {
  const formatted = formatNumber(price, { mantissa: price < 1 ? 4 : 2 })
  if (!formatted || formatted.startsWith('ERROR')) {
    return '$0.00'
  }
  return `$${formatted}`
}
